import { Fragment } from "react";
import { NavLink } from "react-router-dom";

import { useAuth } from "../../lib/auth";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

import "./layout.css";

interface NavItem {
  to: string;
  labelKey: string;
}

interface NavSection {
  id: string;
  labelKey: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    id: "operate",
    labelKey: "nav.section.operate",
    items: [
      { to: "/dashboard", labelKey: "nav.dashboard" },
      { to: "/devices", labelKey: "nav.devices" },
      { to: "/employees", labelKey: "nav.employees" },
    ],
  },
  {
    id: "insights",
    labelKey: "nav.section.insights",
    items: [{ to: "/insights", labelKey: "nav.insights" }],
  },
  {
    id: "govern",
    labelKey: "nav.section.govern",
    items: [{ to: "/policies", labelKey: "nav.policies" }],
  },
  {
    id: "admin",
    labelKey: "nav.section.admin",
    items: [{ to: "/config", labelKey: "nav.config" }],
  },
];

function initials(value: string | null | undefined, fallback = "?"): string {
  if (!value) return fallback;
  const parts = value
    .trim()
    .split(/[\s.@_-]+/u)
    .filter(Boolean);
  if (parts.length === 0) return fallback;
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return (first + second).toUpperCase() || fallback;
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  useLocale();
  const { user } = useAuth();
  const brandName = t("app.title");
  const brandLogo = initials(brandName, "A").slice(0, 1);
  const userName = user?.full_name || user?.email || null;
  const userInitials = initials(userName);
  const userRole = user?.role ? t(`users.roles.${user.role}`) : t("app.subtitle");

  return (
    <aside className="app-shell__sidebar" aria-label={t("nav.dashboard")}>
      <div className="sidebar__brand">
        <div className="sidebar__logo" aria-hidden="true">
          {brandLogo}
        </div>
        <div className="sidebar__brand-block">
          <div className="sidebar__brand-title">{brandName}</div>
          <div className="sidebar__brand-subtitle">{t("app.subtitle")}</div>
        </div>
      </div>

      <ul className="sidebar__nav">
        {NAV_SECTIONS.map((section, sectionIndex) => (
          <Fragment key={section.id}>
            <li
              className="sidebar__section-label"
              aria-hidden="true"
              style={sectionIndex === 0 ? { paddingTop: 4 } : undefined}
            >
              {t(section.labelKey)}
            </li>
            {section.items.map((item) => (
              <li key={item.to} className="sidebar__item">
                <NavLink
                  to={item.to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    isActive ? "sidebar__link sidebar__link--active" : "sidebar__link"
                  }
                >
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              </li>
            ))}
          </Fragment>
        ))}
      </ul>

      {user && (
        <div className="sidebar__footer">
          <div className="sidebar__avatar" aria-hidden="true">
            {userInitials}
          </div>
          <div className="sidebar__user-block">
            <div className="sidebar__user-name" title={userName ?? undefined}>
              {userName ?? "—"}
            </div>
            <div className="sidebar__user-role">{userRole}</div>
          </div>
        </div>
      )}
    </aside>
  );
}
